import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { dmy, nfmt, type GamesStats, type GsBrief, type GsCount } from "../../hooks/gamesStats";
import css from "./GamesDonePop.module.css";

/**
 * Дропдаун «подробно об отведённых играх» (26.10.2026, владелец: «по клику на количество уже отведённых игр — дропдаун
 * с подробной статистикой»). Открывается у числа (якорь), закрывается тапом мимо, Esc или ✕. Данные — useGamesStats
 * (тот же запрос держит хозяин, чтобы число на плитке и в окне было одно).
 */
export interface GamesDonePopProps {
  open: boolean;
  anchor: HTMLElement | null;
  stats: GamesStats | null;
  error?: string | null;
  onClose: () => void;
  /** false — без денег (ОО, 26.10 владелец: «у ОО в окне не нужны данные о деньгах — только количество игр»). */
  money?: boolean;
}

const WD = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];
const brief = (x: GsBrief) => `#${x.id} ${dmy(x.date)} ${x.time} ${x.quest}`;

function Bars({ rows, money }: { rows: GsCount[]; money?: boolean }) {
  const max = Math.max(1, ...rows.map((r) => r.games));
  return (
    <div className={css.bars}>
      {rows.map((r) => (
        <div key={r.name} className={css.bar}>
          <span className={css.barName}>{r.name}</span>
          <span className={css.barNum}>{r.games}{money ? <i> · {nfmt(r.revenue)} ₽</i> : null}</span>
          <span className={css.barTrack}><span className={css.barFill} style={{ width: `${Math.round((r.games / max) * 100)}%` }} /></span>
        </div>
      ))}
    </div>
  );
}

function Line({ label, value, strong, note, ids }: { label: string; value: ReactNode; strong?: boolean; note?: string; ids?: GsBrief[] }) {
  return (
    <div className={`${css.line} ${strong ? css.lineStrong : ""}`}>
      <span>{label}{note ? <small className={css.note}> — {note}</small> : null}</span>
      <b>{value}</b>
      {ids && ids.length ? <small className={css.ids}>{ids.slice(0, 12).map(brief).join(" · ")}{ids.length > 12 ? ` · ещё ${ids.length - 12}` : ""}</small> : null}
    </div>
  );
}

export function GamesDonePop({ open, anchor, stats, error, onClose, money = true }: GamesDonePopProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchor) return;
    const place = () => {
      const r = anchor.getBoundingClientRect();
      const vw = window.innerWidth, vh = window.innerHeight, w = Math.min(380, vw - 16);
      const left = Math.max(8, Math.min(r.left, vw - w - 8));
      const below = vh - r.bottom - 14, above = r.top - 14;
      /* высота — ровно по свободному месту: окно не уходит за край экрана (на низком экране шапка с ✕ иначе пряталась) */
      setPos(below >= 260 || below >= above ? { left, top: r.bottom + 6, maxHeight: Math.max(0, below) } : { left, bottom: vh - r.top + 6, maxHeight: Math.max(0, above) });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open, anchor]);

  useEffect(() => {
    if (!open) return;
    const down = (e: PointerEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchor?.contains(t)) return;
      onClose();
    };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    /* прокрутка страницы уводит число от окна — закрываем, как общий Dropdown; своя прокрутка окна — нет */
    const scroll = (e: Event) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    document.addEventListener("pointerdown", down, true);
    document.addEventListener("keydown", key);
    document.addEventListener("scroll", scroll, true);
    return () => {
      document.removeEventListener("pointerdown", down, true); document.removeEventListener("keydown", key); document.removeEventListener("scroll", scroll, true);
    };
  }, [open, anchor, onClose]);

  if (!open || !pos) return null;
  const s = stats, p = s?.period;
  const wdMax = Math.max(1, ...(s?.weekday ?? [0]));
  return createPortal(
    <div ref={ref} className={css.pop} role="dialog" aria-label="Подробно об отведённых играх"
      style={{ left: pos.left, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}>
      <div className={css.head}>
        <div>
          <div className={css.title}>{s ? `Отведённых игр: ${s.games}` : "Отведённые игры"}</div>
          {s ? <div className={css.period}>{dmy(s.from)}.{s.from.slice(0, 4)} — {dmy(s.to)}.{s.to.slice(0, 4)}</div> : null}
        </div>
        <button type="button" className={css.close} onClick={onClose} aria-label="Закрыть">✕</button>
      </div>
      {error ? <div className={css.empty}>Ошибка: {error}</div> : !s || !p ? <div className={css.empty}>Считаю…</div> : (
        <>
          <div className={css.kpis}>
            {money ? <div><b>{nfmt(s.revenue)} ₽</b><span>выручка</span></div> : <div><b>{p.booked}</b><span>броней</span></div>}
            {money ? <div><b>{nfmt(s.avgCheck)} ₽</b><span>средний чек</span></div> : <div><b>{p.forecast}</b><span>прогноз</span></div>}
            <div><b>{s.players}</b><span>игроков</span></div>
          </div>

          <div className={css.sec}>Из чего состоит период</div>
          <Line label="Отведено" value={s.games} strong note={money ? "основной отчёт с суммой" : "есть отчёт основного оператора"} />
          {p.todayPending ? <Line label="Сегодня, ждут отчёта" value={`+${p.todayPending}`} /> : null}
          <Line label="Впереди" value={`+${p.ahead}`} note="брони без отчёта, ещё не начались" />
          <Line label="Прогноз к концу периода" value={p.forecast} strong />
          {p.pastNoReport.length ? <Line label="Прошли без отчёта" value={p.pastNoReport.length} ids={p.pastNoReport} /> : null}
          {p.zeroReport.length ? <Line label={money ? "Отчёт на 0 ₽ — не в счёте" : "Отчёт без оплаты — не в счёте"} value={p.zeroReport.length} ids={p.zeroReport} /> : null}
          {p.learning.length ? <Line label="Учебные — не в счёте" value={p.learning.length} ids={p.learning} /> : null}
          {p.learningDone.length ? <Line label="Учебные с задатком — уже в «отведено»" value={p.learningDone.length} ids={p.learningDone} /> : null}
          <Line label="Отменено — не в счёте" value={p.cancelled} />
          {p.leadsOpen.length ? <Line label="Лиды в работе — не в счёте" value={p.leadsOpen.length} note="пока ДК не превратит в бронь" ids={p.leadsOpen} /> : null}
          {p.leadsClosed ? <Line label="Лиды закрыты" value={p.leadsClosed} note="превращены в брони или удалены" /> : null}
          {p.archived ? <Line label="В архиве (тесты, убранные)" value={p.archived} /> : null}
          <Line label="Броней в периоде" value={p.booked} note={`игроков по броням: ${p.bookedPlayers}`} />

          {s.byBranch.length > 1 ? <><div className={css.sec}>По филиалам</div><Bars rows={s.byBranch} money={money} /></> : null}
          {s.bySource.length ? <><div className={css.sec}>Откуда пришли</div><Bars rows={s.bySource} /></> : null}
          {s.byWeek.length > 1 ? <><div className={css.sec}>По неделям</div><Bars rows={s.byWeek.map((w) => ({ ...w, name: `с ${dmy(w.name)}` }))} money={money} /></> : null}
          {s.games ? (
            <>
              <div className={css.sec}>По дням недели</div>
              <div className={css.wd}>
                {s.weekday.map((n, i) => (
                  <div key={WD[i]} className={css.wdCol}>
                    <span className={css.wdNum}>{n}</span>
                    <span className={css.wdTrack}><span className={css.wdFill} style={{ height: `${Math.round((n / wdMax) * 100)}%` }} /></span>
                    <span className={css.wdName}>{WD[i]}</span>
                  </div>
                ))}
              </div>
              <div className={css.sec}>Особые</div>
              <div className={css.chips}>
                <span>ночных (22:00–06:00): <b>{s.extras.night}</b></span>
                <span>с мастер-классом: <b>{s.extras.withMc}</b></span>
                <span>с чайной: <b>{s.extras.withTea}</b></span>
                <span>дней рождения: <b>{s.extras.birthday}</b></span>
                <span>спецверсий: <b>{s.extras.special}</b></span>
              </div>
            </>
          ) : null}
          {s.byQuest.length ? <><div className={css.sec}>По квестам</div><Bars rows={s.byQuest} money={money} /></> : null}
        </>
      )}
    </div>,
    document.body,
  );
}
